import { useEffect, useMemo, useState } from 'react';
import { Button, Row } from '@/components/atoms';
import DateRangePicker from '@/components/atoms/date/DateRangePicker';
import { FORMAT_DATE_GENERAL_EXCEPT_TIME } from '@/constants/constant';
import moment, { Moment } from 'moment';
import { Col, Radio, Input } from 'antd';
import TaxNoticeStatus, { allCases as allStatus, messages } from '@/components/covalent/tax/notice/TaxNoticeStatus';
import TaxDateRange, { allCases as allDateRanges, filterFrom, filterTo, messageOf as dateRangeLabelOf } from '@/components/covalent/tax/TaxDateRange';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { FilterStateType } from '@/components/pages/taxNotice/TaxNoticeListPage';

const { Search } = Input;

type TaxNoticeListFilterProps = {
  downloadable?: boolean;
  count: number;
  filter: FilterStateType;
  onSearch: (keyword: string) => void;
  onChangeSearch?: (keyword: string) => void;
  onChangeDateRange?: (from?: string, to?: string, date?: number) => void;
  onChangeFilter?: (status: TaxNoticeStatus) => void;
  onClickDownload: () => void;
  onClickUpload: () => void;
  onClickSendAuto: () => void;
};

export enum FilterDateRange {
  WHOLE,
  MONTH_ONE,
  MONTH_THREE,
  MONTH_LAST,
  CUSTOM,
}

const TaxNoticeListFilter = ({
  downloadable,
  filter,
  count,
  onSearch,
  onChangeSearch,
  onChangeDateRange,
  onChangeFilter,
  onClickDownload,
  onClickUpload,
  onClickSendAuto,
}: TaxNoticeListFilterProps) => {
  const [dateRange, setDateRange] = useState<any>();
  const [dateRangeState, setDateRangeState] = useState(filter.date ?? TaxDateRange.WHOLE);
  const [keyword, setKeyword] = useState(filter.keyword ?? '');

  const isDatePickerVisible = useMemo(() => {
    return dateRangeState === TaxDateRange.CUSTOM;
  }, [dateRangeState]);

  useEffect(() => {
    if (onChangeSearch) {
      onChangeSearch(keyword);
    }
  }, [keyword, onChangeSearch]);

  return (
    <Row className="list-function">
      <Col flex={1} className="filter">
        <Row>
          <Radio.Group
            options={allStatus.map((status) => ({
              value: status,
              label: `${messages(status)}`,
            }))}
            optionType="button"
            buttonStyle="outline"
            defaultValue={filter.status ?? TaxNoticeStatus.ALL}
            onChange={({ target }) => {
              if (onChangeFilter) {
                onChangeFilter(target.value);
              }
            }}
          />
        </Row>
        <Row>
          <Radio.Group
            options={allDateRanges.map((item) => ({
              value: item,
              label: dateRangeLabelOf(item),
            }))}
            value={dateRangeState}
            optionType="button"
            buttonStyle="outline"
            onChange={({ target }) => {
              if (onChangeDateRange && target.value !== FilterDateRange.CUSTOM) {
                const from = filterFrom(target.value);
                const to = filterTo(target.value, from);
                onChangeDateRange(from?.format(FORMAT_DATE_GENERAL_EXCEPT_TIME), to?.format(FORMAT_DATE_GENERAL_EXCEPT_TIME), target.value);
              }
              setDateRangeState(target.value);
            }}
          />
          {isDatePickerVisible && (
            <DateRangePicker
              value={dateRange}
              disabledDate={(date: Moment) => {
                if (dateRange && dateRange[0]) {
                  const [from] = dateRange;
                  return date.isAfter(moment(from, FORMAT_DATE_GENERAL_EXCEPT_TIME).add(3, 'months'));
                }
                return date.isAfter(moment());
              }}
              onChange={(range) => {
                setDateRange(range);
                if (onChangeDateRange && range) {
                  const [from, to] = range;
                  onChangeDateRange(from?.format(FORMAT_DATE_GENERAL_EXCEPT_TIME), to?.format(FORMAT_DATE_GENERAL_EXCEPT_TIME));
                }
              }}
            />
          )}
        </Row>
        <Row>
          <Search
            placeholder="발급기관/차량번호"
            style={{ width: 300 }}
            value={keyword}
            onSearch={onSearch}
            onChange={({ target }) => {
              setKeyword(target.value);
            }}
          />
        </Row>
      </Col>
      <Col>
        <Row>
          <Button type="dark" shape="round" onClick={onClickUpload} icon={<PlusOutlined />}>
            고지서 등록
          </Button>
        </Row>
        <Row>
          <Button type="default" shape="round" onClick={onClickDownload} disabled={!downloadable} icon={<DownloadOutlined />}>
            엑셀 다운로드
          </Button>
        </Row>
        <Row>
          <Button type="success" shape="round" onClick={onClickSendAuto} disabled={count === 0}>
            자동 민원신청({count}건)
          </Button>
        </Row>
      </Col>
    </Row>
  );
};

TaxNoticeListFilter.defaultProps = {
  downloadable: false,
  onChangeSearch: undefined,
  onChangeDateRange: undefined,
  onChangeFilter: undefined,
};

export default TaxNoticeListFilter;
